// Comercial › Painel › Roteiros comerciais: biblioteca de roteiros de conversa (reunião, ligação, mensagens).
// Lista à esquerda, roteiro aberto à direita; a edição é na própria página (sem modal). Ações `rot_*` em ./api.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, App, Button, Col, Divider, Drawer, Empty, Form, Grid, Input, List, Popconfirm, Row, Select, Space, Spin, Tabs, Tag, Typography,
} from 'antd';
import MarkdownSeguro from '../nexo/MarkdownSeguro';
import '../nexo/Nexo.css';
import './Roteiros.css';
import { ehConflito, ehEmInstalacao, ehSemAcesso, erroDe, listar, restaurar, salvar, versao as obterVersao, versoes as listarVersoes } from './api';
import {
  CANAIS, COR_DO_STATUS, filtrarRoteiros, fmtDataHora, formularioDe, formularioVazio, LIMITES, ordenar, resumoDoTexto, ROTULOS, rotuloDe,
  STATUS, TIPOS, validarFormulario,
  type Formulario, type Listagem, type VersaoCompleta, type VersaoResumo,
} from './regras';

const AJUDA_FORMATACAO = 'Formatação: ## título · - lista · 1. lista numerada · | tabela | · > fala para o cliente · **negrito**';

type Falha = 'acesso' | 'instalando' | 'outro' | null;

const idDaUrl = (): number | null => {
  try {
    const n = Number(new URLSearchParams(window.location.search).get('roteiro'));
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch { return null; }
};

const atualizarUrl = (id: number | null) => {
  try {
    const u = new URL(window.location.href);
    if (id == null) u.searchParams.delete('roteiro'); else u.searchParams.set('roteiro', String(id));
    window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`);
  } catch { /* a URL é só conveniência */ }
};

const opcoes = (codigos: string[], rotulos: Record<string, string>) => codigos.map((c) => ({ value: c, label: rotulos[c] ?? c }));

export default function Roteiros() {
  const { message } = App.useApp();
  const screens = Grid.useBreakpoint();
  const duasColunas = !!screens.lg;
  const editorLadoALado = !!screens.xl;

  const [carregando, setCarregando] = useState(true);
  const [falha, setFalha] = useState<Falha>(null);
  const [falhaTexto, setFalhaTexto] = useState('');
  const [dados, setDados] = useState<Listagem | null>(null);

  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<string | undefined>();
  const [filtroCanal, setFiltroCanal] = useState<string | undefined>();
  const [filtroStatus, setFiltroStatus] = useState<string | undefined>();

  const [selId, setSelId] = useState<number | null>(null);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<Formulario>(formularioVazio());
  const [errosForm, setErrosForm] = useState<string[]>([]);
  const [erroSalvar, setErroSalvar] = useState<{ texto: string; conflito: boolean } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [abaEditor, setAbaEditor] = useState('texto');

  const [versoesAbertas, setVersoesAbertas] = useState(false);
  const [listaVersoes, setListaVersoes] = useState<VersaoResumo[] | null>(null);
  const [erroVersoes, setErroVersoes] = useState('');
  const [carregandoVersoes, setCarregandoVersoes] = useState(false);
  const [versaoVista, setVersaoVista] = useState<VersaoCompleta | null>(null);
  const [carregandoVista, setCarregandoVista] = useState(false);
  const [restaurando, setRestaurando] = useState(false);

  const idInicial = useRef<number | null>(idDaUrl());

  const rotulos = dados?.rotulos ?? ROTULOS;
  const podeEditar = !!dados?.pode.editar;
  const roteiro = useMemo(() => (selId == null ? null : dados?.roteiros.find((x) => x.id === selId) ?? null), [dados, selId]);
  const novo = editando && selId == null;

  const carregar = useCallback(async (abrir: number | null = null) => {
    setCarregando(true);
    setFalha(null);
    try {
      const r = await listar();
      const lista = ordenar(r.roteiros ?? []);
      setDados({ pode: r.pode ?? { editar: false }, roteiros: lista, rotulos: r.rotulos ?? ROTULOS });
      if (abrir != null && lista.some((x) => x.id === abrir)) {
        setSelId(abrir);
        atualizarUrl(abrir);
      } else if (abrir != null) {
        atualizarUrl(null);
      }
    } catch (e) {
      if (ehSemAcesso(e)) setFalha('acesso');
      else if (ehEmInstalacao(e)) setFalha('instalando');
      else { setFalha('outro'); setFalhaTexto(erroDe(e)); }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(idInicial.current); }, [carregar]);

  const visiveis = useMemo(
    () => filtrarRoteiros(dados?.roteiros ?? [], { busca, tipo: filtroTipo, canal: filtroCanal, status: filtroStatus }),
    [dados, busca, filtroTipo, filtroCanal, filtroStatus],
  );

  // ── Navegação ──────────────────────────────────────────────────────────────
  const abrir = (id: number) => {
    setSelId(id);
    setEditando(false);
    setErrosForm([]);
    setErroSalvar(null);
    atualizarUrl(id);
  };

  const voltarALista = () => {
    setSelId(null);
    setEditando(false);
    setErrosForm([]);
    setErroSalvar(null);
    atualizarUrl(null);
  };

  const comecarNovo = () => {
    setSelId(null);
    setForm(formularioVazio());
    setErrosForm([]);
    setErroSalvar(null);
    setAbaEditor('texto');
    setEditando(true);
    atualizarUrl(null);
  };

  const comecarEdicao = () => {
    if (!roteiro) return;
    setForm(formularioDe(roteiro));
    setErrosForm([]);
    setErroSalvar(null);
    setAbaEditor('texto');
    setEditando(true);
  };

  const cancelar = () => {
    setEditando(false);
    setErrosForm([]);
    setErroSalvar(null);
  };

  const mexer = (parte: Partial<Formulario>) => setForm((f) => ({ ...f, ...parte }));

  // ── Salvar ─────────────────────────────────────────────────────────────────
  const gravar = async () => {
    const erros = validarFormulario(form);
    setErrosForm(erros);
    setErroSalvar(null);
    if (erros.length) return;
    setSalvando(true);
    try {
      const corpo = { ...form, titulo: form.titulo.trim() };
      const r = roteiro
        ? await salvar({ ...corpo, id: roteiro.id, visto: { atualizado_em: roteiro.atualizado_em } })
        : await salvar(corpo);
      const gravado = r.roteiro;
      setDados((d) => (d ? { ...d, roteiros: ordenar([...d.roteiros.filter((x) => x.id !== gravado.id), gravado]) } : d));
      setSelId(gravado.id);
      setEditando(false);
      atualizarUrl(gravado.id);
      message.success('Roteiro salvo.');
    } catch (e) {
      setErroSalvar({ texto: erroDe(e), conflito: ehConflito(e) });
    } finally {
      setSalvando(false);
    }
  };

  const recarregarAberto = async () => {
    setEditando(false);
    setErroSalvar(null);
    setErrosForm([]);
    await carregar(selId);
  };

  // ── Copiar ─────────────────────────────────────────────────────────────────
  const copiar = async (texto: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(texto);
        message.success('Texto copiado');
        return;
      }
    } catch { /* cai no plano B */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) message.success('Texto copiado'); else message.error('Não deu para copiar. Selecione o texto e copie à mão.');
    } catch {
      message.error('Não deu para copiar. Selecione o texto e copie à mão.');
    }
  };

  // ── Versões ────────────────────────────────────────────────────────────────
  const abrirVersoes = async () => {
    if (!roteiro) return;
    setVersoesAbertas(true);
    setVersaoVista(null);
    setListaVersoes(null);
    setErroVersoes('');
    setCarregandoVersoes(true);
    try {
      const r = await listarVersoes(roteiro.id);
      setListaVersoes(r.versoes ?? []);
    } catch (e) {
      setErroVersoes(erroDe(e));
    } finally {
      setCarregandoVersoes(false);
    }
  };

  const verVersao = async (numero: number) => {
    if (!roteiro) return;
    setCarregandoVista(true);
    setErroVersoes('');
    try {
      const r = await obterVersao(roteiro.id, numero);
      setVersaoVista(r.versao);
    } catch (e) {
      setErroVersoes(erroDe(e));
    } finally {
      setCarregandoVista(false);
    }
  };

  const restaurarVersao = async (numero: number) => {
    if (!roteiro) return;
    setRestaurando(true);
    setErroVersoes('');
    try {
      const r = await restaurar(roteiro.id, numero, { atualizado_em: roteiro.atualizado_em });
      const gravado = r.roteiro;
      setDados((d) => (d ? { ...d, roteiros: ordenar([...d.roteiros.filter((x) => x.id !== gravado.id), gravado]) } : d));
      setVersoesAbertas(false);
      setVersaoVista(null);
      message.success(`Versão ${numero} restaurada.`);
    } catch (e) {
      setErroVersoes(erroDe(e));
    } finally {
      setRestaurando(false);
    }
  };

  // ── Peças da tela ──────────────────────────────────────────────────────────
  const etiquetas = (r: { tipo: string; canal: string | null; status: string }) => (
    <Space size={[4, 4]} wrap>
      <Tag color="blue" style={{ marginInlineEnd: 0 }}>{rotuloDe(rotulos.tipo, r.tipo)}</Tag>
      {r.canal ? <Tag style={{ marginInlineEnd: 0 }}>{rotuloDe(rotulos.canal, r.canal)}</Tag> : null}
      <Tag color={COR_DO_STATUS[r.status] ?? 'default'} style={{ marginInlineEnd: 0 }}>{rotuloDe(rotulos.status, r.status)}</Tag>
    </Space>
  );

  const painelLista = (
    <div style={{ width: duasColunas ? 340 : '100%', flex: duasColunas ? '0 0 340px' : '1 1 auto', minWidth: 0 }}>
      <Space orientation="vertical" size={10} style={{ width: '100%' }}>
        {podeEditar ? <Button type="primary" block onClick={comecarNovo}>Novo roteiro</Button> : null}
        <Input.Search
          placeholder="Buscar roteiro" allowClear value={busca}
          onChange={(e) => setBusca(e.target.value)} onSearch={setBusca}
        />
        <Select
          style={{ width: '100%' }} placeholder="Tipo" allowClear showSearch optionFilterProp="label"
          value={filtroTipo} onChange={(v) => setFiltroTipo(v ?? undefined)} options={opcoes(TIPOS, rotulos.tipo)}
        />
        <Select
          style={{ width: '100%' }} placeholder="Canal" allowClear showSearch optionFilterProp="label"
          value={filtroCanal} onChange={(v) => setFiltroCanal(v ?? undefined)} options={opcoes(CANAIS, rotulos.canal)}
        />
        <Select
          style={{ width: '100%' }} placeholder="Status (arquivados ficam escondidos)" allowClear showSearch optionFilterProp="label"
          value={filtroStatus} onChange={(v) => setFiltroStatus(v ?? undefined)} options={opcoes(STATUS, rotulos.status)}
        />
        {dados && dados.roteiros.length === 0 ? (
          <Empty description="Nenhum roteiro. Crie o primeiro." />
        ) : (
          <div className="roteiros-lista-rolagem">
            <List
              size="small"
              dataSource={visiveis}
              locale={{ emptyText: <Empty description="Nenhum roteiro com esses filtros." /> }}
              renderItem={(r) => (
                <List.Item
                  key={r.id}
                  className={`roteiros-lista-item${r.id === selId ? ' ativo' : ''}`}
                  onClick={() => abrir(r.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(r.id); } }}
                >
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <Typography.Text strong style={{ display: 'block', overflowWrap: 'anywhere' }}>{r.titulo}</Typography.Text>
                    <div style={{ marginTop: 4 }}>{etiquetas(r)}</div>
                    <p className="roteiros-resumo">{resumoDoTexto(r.conteudo, 140)}</p>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </Space>
    </div>
  );

  const leitura = roteiro ? (
    <div>
      <Space orientation="vertical" size={8} style={{ width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0, overflowWrap: 'anywhere' }}>{roteiro.titulo}</Typography.Title>
        {etiquetas(roteiro)}
        {roteiro.publico ? <Typography.Text><strong>Para quem:</strong> {roteiro.publico}</Typography.Text> : null}
        {roteiro.objetivo ? <Typography.Text><strong>Objetivo:</strong> {roteiro.objetivo}</Typography.Text> : null}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Versão {roteiro.versao} · atualizado em {fmtDataHora(roteiro.atualizado_em)}{roteiro.atualizado_por ? ` por ${roteiro.atualizado_por}` : ''}
        </Typography.Text>
        <Space wrap>
          <Button type="primary" onClick={() => copiar(roteiro.conteudo)}>Copiar texto</Button>
          {podeEditar ? <Button onClick={comecarEdicao}>Editar</Button> : null}
          {podeEditar ? <Button onClick={abrirVersoes}>Versões</Button> : null}
        </Space>
      </Space>
      <Divider style={{ margin: '14px 0 0' }} />
      <div className="roteiros-corpo"><MarkdownSeguro conteudo={roteiro.conteudo} /></div>
    </div>
  ) : (
    <Empty description="Escolha um roteiro na lista." style={{ marginTop: 40 }} />
  );

  const campoTexto = (
    <div className="roteiros-texto">
      <Input.TextArea
        value={form.conteudo} onChange={(e) => mexer({ conteudo: e.target.value })}
        autoSize={{ minRows: 18 }} placeholder="Escreva o roteiro aqui."
      />
      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
        {form.conteudo.length.toLocaleString('pt-BR')} de {LIMITES.conteudo.toLocaleString('pt-BR')} letras
      </Typography.Text>
    </div>
  );

  const previa = (
    <div className="roteiros-previa">
      {form.conteudo.trim()
        ? <MarkdownSeguro conteudo={form.conteudo} />
        : <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>A prévia aparece aqui enquanto você escreve.</Typography.Text>}
    </div>
  );

  const edicao = (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>{novo ? 'Novo roteiro' : 'Editar roteiro'}</Typography.Title>
      <Form layout="vertical">
        <Form.Item label="Título" required>
          <Input value={form.titulo} onChange={(e) => mexer({ titulo: e.target.value })} maxLength={LIMITES.titulo + 40} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Tipo" required>
              <Select
                value={form.tipo} onChange={(v) => mexer({ tipo: v })} options={opcoes(TIPOS, rotulos.tipo)}
                showSearch optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Canal">
              <Select
                value={form.canal || undefined} onChange={(v) => mexer({ canal: v ?? '' })} options={opcoes(CANAIS, rotulos.canal)}
                allowClear showSearch optionFilterProp="label" placeholder="Sem canal"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Status">
              <Select
                value={form.status} onChange={(v) => mexer({ status: v })} options={opcoes(STATUS, rotulos.status)}
                showSearch optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Para quem">
          <Input value={form.publico} onChange={(e) => mexer({ publico: e.target.value })} placeholder="Ex.: gerente de operações de saneamento" />
        </Form.Item>
        <Form.Item label="Objetivo">
          <Input.TextArea rows={2} value={form.objetivo} onChange={(e) => mexer({ objetivo: e.target.value })} placeholder="O que esta conversa precisa conseguir" />
        </Form.Item>
        <Form.Item label="Texto" required extra={AJUDA_FORMATACAO}>
          {editorLadoALado ? (
            <Row gutter={16}>
              <Col span={12}>{campoTexto}</Col>
              <Col span={12}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>Prévia</Typography.Text>
                {previa}
              </Col>
            </Row>
          ) : (
            <Tabs
              activeKey={abaEditor} onChange={setAbaEditor}
              items={[
                { key: 'texto', label: 'Texto', children: campoTexto },
                { key: 'previa', label: 'Prévia', children: previa },
              ]}
            />
          )}
        </Form.Item>
      </Form>
      {errosForm.length ? (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }} title="Confira antes de salvar"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{errosForm.map((e) => <li key={e}>{e}</li>)}</ul>}
        />
      ) : null}
      {erroSalvar ? (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }} title={erroSalvar.texto}
          action={erroSalvar.conflito ? <Button size="small" onClick={recarregarAberto}>Recarregar</Button> : undefined}
        />
      ) : null}
      <Space wrap>
        <Button type="primary" loading={salvando} onClick={gravar}>Salvar</Button>
        <Button onClick={cancelar} disabled={salvando}>Cancelar</Button>
      </Space>
    </div>
  );

  const painelDireito = (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      {!duasColunas ? (
        <Button style={{ marginBottom: 12 }} onClick={editando ? cancelar : voltarALista}>
          {editando && !novo ? 'Cancelar e voltar ao roteiro' : 'Voltar à lista'}
        </Button>
      ) : null}
      {editando ? edicao : leitura}
    </div>
  );

  const drawerVersoes = (
    <Drawer
      open={versoesAbertas} onClose={() => { setVersoesAbertas(false); setVersaoVista(null); }}
      size="min(520px, 94vw)" destroyOnHidden styles={{ body: { overflowY: 'auto' } }}
      title={versaoVista ? `Versão ${versaoVista.versao}` : 'Versões'}
    >
      {erroVersoes ? <Alert type="error" showIcon style={{ marginBottom: 12 }} title={erroVersoes} /> : null}
      {versaoVista ? (
        <div>
          <Space wrap style={{ marginBottom: 12 }}>
            <Button onClick={() => setVersaoVista(null)}>Voltar</Button>
            <Popconfirm
              title="Restaurar esta versão?" description="A versão atual vai para o histórico."
              okText="Restaurar" cancelText="Cancelar" onConfirm={() => restaurarVersao(versaoVista.versao)}
            >
              <Button type="primary" loading={restaurando}>Restaurar esta versão</Button>
            </Popconfirm>
          </Space>
          <Typography.Title level={4} style={{ marginTop: 0, overflowWrap: 'anywhere' }}>{versaoVista.titulo}</Typography.Title>
          {etiquetas(versaoVista)}
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Gravada em {fmtDataHora(versaoVista.gravado_em)}{versaoVista.gravado_por ? ` por ${versaoVista.gravado_por}` : ''}
            </Typography.Text>
          </div>
          {versaoVista.publico ? <div style={{ marginTop: 8 }}><strong>Para quem:</strong> {versaoVista.publico}</div> : null}
          {versaoVista.objetivo ? <div style={{ marginTop: 4 }}><strong>Objetivo:</strong> {versaoVista.objetivo}</div> : null}
          <Divider style={{ margin: '12px 0 0' }} />
          <div className="roteiros-corpo"><MarkdownSeguro conteudo={versaoVista.conteudo} /></div>
        </div>
      ) : carregandoVersoes || carregandoVista ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : listaVersoes && listaVersoes.length === 0 ? (
        <Empty description="Este roteiro ainda não foi alterado." />
      ) : listaVersoes ? (
        <List
          dataSource={listaVersoes}
          renderItem={(v) => (
            <List.Item key={v.versao} actions={[<Button key="ver" size="small" onClick={() => verVersao(v.versao)}>Ver</Button>]}>
              <List.Item.Meta
                title={<span style={{ overflowWrap: 'anywhere' }}>{v.titulo}</span>}
                description={`Versão ${v.versao} · ${fmtDataHora(v.gravado_em)}${v.gravado_por ? ` · ${v.gravado_por}` : ''} · ${v.letras.toLocaleString('pt-BR')} letras`}
              />
            </List.Item>
          )}
        />
      ) : null}
    </Drawer>
  );

  // ── Estados ────────────────────────────────────────────────────────────────
  if (carregando && !dados) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (falha === 'acesso') {
    return <div style={{ padding: 16 }}><Alert type="warning" showIcon title="Você não tem acesso a esta página." /></div>;
  }
  if (falha === 'instalando') {
    return <div style={{ padding: 16 }}><Alert type="info" showIcon title="A página ainda está sendo instalada." /></div>;
  }
  if (falha === 'outro' && !dados) {
    return (
      <div style={{ padding: 16 }}>
        <Alert type="error" showIcon title={falhaTexto} action={<Button size="small" onClick={() => carregar(idInicial.current)}>Tentar de novo</Button>} />
      </div>
    );
  }

  const mostrarLista = duasColunas || (selId == null && !editando);

  return (
    <div style={{ padding: 16 }}>
      {falha === 'outro' ? <Alert type="error" showIcon style={{ marginBottom: 12 }} title={falhaTexto} /> : null}
      <Spin spinning={carregando}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
          {mostrarLista ? painelLista : null}
          {duasColunas || !mostrarLista ? painelDireito : null}
        </div>
      </Spin>
      {drawerVersoes}
    </div>
  );
}
